import { Button, theme } from 'antd'
import { observer } from 'mobx-react-lite'
import { Fragment, useEffect } from 'react'
import { FaCheck } from 'react-icons/fa'
import { LuBadgeMinus, LuChevronRight } from 'react-icons/lu'

import { LossReasonModal } from '@/Containers/TransportationSection/Components'
import { statusDictionary } from '@/Ship/Components/Filters/StatusFilterBar'
import { Hint } from '@/Ship/Components/Hint'

import './ApplicationStatusBar.css'

import { APPLICATION_TEXTS as T } from '../Contracts/ApplicationFieldLabels'
import { ApplicationStepRequirements } from './ApplicationStepRequirements'
import { APPLICATION_STATUS_CODES, APPLICATION_STATUS_RESOURCE, APPLICATION_STATUS_STEPS } from '../Contracts/IApplication'
import { APPLICATION_TEXT_MAX_LENGTH, applicationActionsStoreFor } from '../Stores/ApplicationActionsStore'

import type { ApplicationCrudContextLike, ApplicationFormContextLike } from '../Contracts/IApplication'
import type { ApplicationActionsStore } from '../Stores/ApplicationActionsStore'

interface Props {
    crudContext: ApplicationCrudContextLike
    formContext: ApplicationFormContextLike
}

const LoseDialog = observer(({ store }: { store: ApplicationActionsStore }) => (
    <LossReasonModal
        open={store.modal === 'lose'}
        options={store.reasons}
        loading={store.reasonsLoading}
        value={store.lossReason}
        onChange={(value) => store.setLossReason(value)}
        withComment
        comment={store.lossComment}
        onCommentChange={(value) => store.setLossComment(value)}
        commentMaxLength={APPLICATION_TEXT_MAX_LENGTH}
        fieldError={store.fieldError}
        error={store.error}
        pending={store.pending}
        onSubmit={() => void store.submit()}
        onCancel={() => store.close()}
    />
))

/**
 * Смуга статусів картки (`SPEC-021` §8.4; легасі `StatusBarDetail.jsx`).
 *
 *  - **кроки — у порядку коду** (`ApplicationStatusCode`, ДП-14), а не за
 *    `sorting` довідника; «Програш» — не крок. Назви й кольори — з довідника;
 *  - **пройдені** — кольором поточного з галочкою, **поточний** — заливкою,
 *    **майбутні** клікаються: біля кроку відкривається вікно з переліком того,
 *    чого бракує до нього (ДЗ-08); картку при цьому не чіпаємо;
 *  - **«Програти»** — праворуч, поки заявка не програна, за `_permissions.lose`.
 *
 * Перелік — лише в збереженій заявці й у режимі перегляду.
 */
export const ApplicationStatusBar = observer(({ crudContext, formContext }: Props) => {
    const { token } = theme.useToken()
    const dictionary = statusDictionary(APPLICATION_STATUS_RESOURCE)
    const store = applicationActionsStoreFor(crudContext)

    useEffect(() => {
        if (!dictionary.isLoaded) void dictionary.refresh()
    }, [dictionary])

    const status = formContext.values?.status
    const currentCode = typeof status?.code === 'string' ? status.code : null
    const isLost = currentCode === APPLICATION_STATUS_CODES.loss
    const currentIndex = currentCode ? APPLICATION_STATUS_STEPS.indexOf(currentCode as (typeof APPLICATION_STATUS_STEPS)[number]) : -1
    const currentColor = status?.color || token.colorPrimary
    const isEditable = formContext.isEditable === true
    const uuid = typeof formContext.values?.uuid === 'string' ? formContext.values.uuid : null
    const canLose = store.permissions.lose === true && !isLost && !isEditable

    const option = (code: string) => dictionary.options.find((item) => item.value === code)

    return (
        <div style={{ marginBottom: 16 }}>
            <Hint id="transport.application.status">
                Статус заявки змінюється сам, коли заповнено потрібні поля і настають дати завантаження й розвантаження.
                Натисніть на наступний крок, щоб побачити, чого бракує.
            </Hint>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }} data-testid="application-status-bar">
                <div style={{ display: 'flex', flex: '1 1 auto', alignItems: 'center', gap: 2, flexWrap: 'wrap', minWidth: 0 }}>
                    {APPLICATION_STATUS_STEPS.map((code, index) => {
                        const title = option(code)?.label ?? code
                        const isCurrent = index === currentIndex
                        const isPassed = !isLost && currentIndex >= 0 && index < currentIndex
                        const isFuture = !isLost && !isCurrent && !isPassed
                        const isClickable = isFuture && !isEditable

                        // Ланцюжок кроків, а не ряд однакових рамок: пройдені — текстом
                        // кольору статусу з галочкою, поточний — заливкою, майбутні —
                        // звичайним текстом, що підсвічується під курсором. Рамки з «i» на
                        // кожному кроці читалися як вимкнені кнопки.
                        const step = (
                            <button
                                type="button"
                                className="wl-application-status-step"
                                disabled={!isClickable}
                                aria-current={isCurrent ? 'step' : undefined}
                                style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: 6,
                                    padding: '4px 10px',
                                    border: 0,
                                    borderRadius: token.borderRadius,
                                    background: isCurrent ? currentColor : 'transparent',
                                    color: isCurrent ? token.colorWhite : isPassed ? currentColor : token.colorTextSecondary,
                                    fontWeight: isCurrent ? 600 : 400,
                                    fontSize: token.fontSize,
                                    lineHeight: '22px',
                                    cursor: isClickable ? 'pointer' : 'default',
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                {isPassed ? <FaCheck /> : null}
                                {title}
                            </button>
                        )

                        return (
                            <Fragment key={code}>
                                {index > 0 ? (
                                    <LuChevronRight aria-hidden style={{ color: token.colorTextQuaternary, flex: 'none' }} />
                                ) : null}
                                {isClickable && uuid ? (
                                    <ApplicationStepRequirements uuid={uuid} code={code} title={title}>
                                        {step}
                                    </ApplicationStepRequirements>
                                ) : (
                                    step
                                )}
                            </Fragment>
                        )
                    })}
                </div>
                {canLose ? (
                    <Button danger type="primary" icon={<LuBadgeMinus />} onClick={() => store.open('lose')}>
                        {T.lose}
                    </Button>
                ) : null}
            </div>
            <LoseDialog store={store} />
        </div>
    )
})
